import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ComponentType } from "react";
import { FormatTools } from "../features/FormatTools/components/FormatTools";
import { runDomAction } from "../features/command-palette/services/runAction";
import {
  isAllActionsOpen,
  requestAllActions,
  subscribeDeckorateCommands,
} from "../../powerpoint/features/command-palette/services/allActionsBridge";
import { requestChatSheet } from "../features/action-chips/services/chatSheetBridge";
import { logError, scrubMessage } from "../../../telemetry";
import { ApplyBrandPanel } from "../features/apply-brand/components/ApplyBrandPanel";
import { BrandCheckPanel } from "../features/brand-check/components/BrandCheckPanel";
import { FixAlignmentPanel } from "../features/FixAlignment/components/FixAlignmentPanel";
import { MapPlot } from "../features/MapPlot/components/MapPlot";
import { OrgChart } from "../features/OrgChart/components/OrgChart";
import { SpeakerNotesPanel } from "../features/SpeakerNotes/components/SpeakerNotesPanel";
import { SlideTransformPanel } from "../features/slide-transform/components/SlideTransformPanel";
import { InfographicPanel } from "../features/Infographic/components/InfographicPanel";
import { PhotoHeadshotPanel } from "../features/PhotoHeadshot/components/PhotoHeadshotPanel";
import { SketchToSlidePanel } from "../features/SketchToSlide/components/SketchToSlidePanel";
import { CreateSlideTab } from "../features/create-slide/components/CreateSlideTab";
import { CompareSlides } from "../features/CompareSlides/components/CompareSlides";
import { SharePanel } from "../features/share-export/components/SharePanel";
import { GetInsightsPanel } from "../features/get-insights/components/GetInsightsPanel";
import { AssetLibraryDialog } from "../features/asset-library/components/AssetLibraryDialog";
import { INSERT_NOTICE_COPY } from "../features/slide-templates/services/slideTemplateInsert";
import { BRAND_TEMPLATE_REMOVED_COPY } from "../features/apply-brand/services/brandTemplatesApi";
import { PRESENTATION_INSERT_NOTICE_COPY } from "../features/presentation-templates/services/presentationTemplateInsert";
import { useAssetLibraryLauncher } from "../features/asset-library/hooks/useAssetLibraryLauncher";
import { subscribeBrandTemplatePreviewRequest } from "../features/asset-library/services/brandTemplatePreviewHandoff";
import { DeckorateHome } from "./deckorateHome/DeckorateHome";
import { isEditableTarget, resolveShortcut } from "../utils/shortcuts";
import { hasOpenDismissLayer } from "../utils/dismissLayers";
import { useCodeMode } from "../codeMode/useCodeMode";
import * as codeModeStore from "../codeMode/store";
import { focusCodeModeCatcher, isFocusCatcher } from "../codeMode/focusCatcher";
import type { CodeModeEntry } from "../codeMode/table";
import { ModuleHeader } from "../../../shared/components/ModuleHeader";
import { DeckorateIcon } from "../../../shared/components/icons";
import styles from "./DeckorateModule.module.css";
import type { DeckorateNav } from "./deckorateHome/ribbonGroups";

// Command Palette's currentTab/onNavigate shape — distinct from `View` so
// palette categories with no real area yet (AIVA-1399) still have a home.
import type { DeckorateCommand, Tab } from "../types";
import { ColourToolsPanel } from "../features/ColorTools/components/ColourToolsPanel";
import {
  consumePendingDeckorateView,
  subscribeDeckorateNav,
} from "../../../../integrations/tools/deckorate";

type View =
  | "home"
  | "apply-brand"
  | "brand-check"
  | "color"
  | "fix-alignment"
  | "format"
  | "infographic"
  | "map"
  | "org-chart"
  | "photo-headshot"
  | "sketch-to-slide"
  | "speaker-notes"
  | "compare"
  | "create-slide"
  | "share-export"
  | "slide-transform"
  | "insights";

// Tab/View literal values naming the same real panel (identical by
// construction — see Tab's doc comment in types.ts). One set drives both
// directions instead of two hand-written if-chains kept in sync manually.
const VIEW_TABS = [
  "color",
  "format",
  "apply-brand",
  "map",
  "brand-check",
  "fix-alignment",
  "infographic",
  "org-chart",
  "photo-headshot",
  "sketch-to-slide",
  "speaker-notes",
  "compare",
  "create-slide",
  "slide-transform",
] as const;
type ViewTab = (typeof VIEW_TABS)[number];

function isViewTab(value: string): value is ViewTab {
  return (VIEW_TABS as readonly string[]).includes(value);
}

interface PanelProps {
  readonly onBack?: () => void;
}

// One panel component per ViewTab — replaces a repetitive if-chain of
// identical `<>{Panel}{palette}{notice}</>` blocks with a single lookup.
const VIEW_PANELS: Readonly<Record<ViewTab, ComponentType<PanelProps>>> = {
  format: FormatTools,
  color: ColourToolsPanel,
  "fix-alignment": FixAlignmentPanel,
  compare: CompareSlides,
  map: MapPlot,
  "org-chart": OrgChart,
  infographic: InfographicPanel,
  "photo-headshot": PhotoHeadshotPanel,
  "sketch-to-slide": SketchToSlidePanel,
  "speaker-notes": SpeakerNotesPanel,
  "create-slide": CreateSlideTab,
  "apply-brand": ApplyBrandPanel,
  "brand-check": BrandCheckPanel,
  "slide-transform": SlideTransformPanel,
};

/** Matches CommandPalette.tsx's/runAction.ts's connectorSystemName. */
const CMD_PALETTE_CONNECTOR = "deckorate";

function logDeckorateModuleError(reason: string, type: string, e: unknown): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    { connectorSystemName: CMD_PALETTE_CONNECTOR, reason },
  );
}

type AssetLibraryNotice = NonNullable<ReturnType<typeof useAssetLibraryLauncher>["paneNotice"]>;
type AssetLibraryNoticeSource = ReturnType<typeof useAssetLibraryLauncher>["paneNoticeSource"];

/** Wording for an insert-failure notice; a removed brand template has its own, the rest follow the source tab. */
function resolveNoticeCopy(notice: AssetLibraryNotice | null, source: AssetLibraryNoticeSource) {
  if (!notice) return null;
  if (notice === "removed" && source === "brand-templates") return BRAND_TEMPLATE_REMOVED_COPY;
  const copy = source === "presentation-templates" ? PRESENTATION_INSERT_NOTICE_COPY : INSERT_NOTICE_COPY;
  return copy[notice];
}

/** Escape/Backspace with no modifier, and not typed into a field (Code Mode's focus catcher excepted). */
function isPlainBackKey(event: KeyboardEvent): boolean {
  if (event.key !== "Escape" && event.key !== "Backspace") return false;
  // A modified key belongs to someone else — Ctrl+Backspace deletes a word, Alt+Left is host navigation.
  if (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) return false;
  return !isTypingIntoField(event);
}

/** True when the key lands in a real field; Code Mode's focus catcher is an input only so PowerPoint hands it focus. */
function isTypingIntoField(event: KeyboardEvent): boolean {
  return !isFocusCatcher(event.target) && isEditableTarget(event.target);
}

interface DeckorateModuleProps {
  readonly onBack?: () => void;
  readonly onOpenSettings?: () => void;
  /**
   * Screen to land on instead of the home grid, for callers that hand off into
   * Deckorate from elsewhere. Accepts any routed view, not just "insights":
   * the chat tab's action chips (AIVA-6561) route to Create Slides, Fix
   * Alignment, Infographic and Speaker Notes as well.
   */
  readonly initialView?: Exclude<DeckorateNav, "asset-library"> | null;
  /** Called once the initial view above has been applied, so the caller can
   * clear its own state — otherwise a later, ordinary visit to this tab
   * (TabBar click, pinned tile) would keep reopening the same view. */
  readonly onInitialViewConsumed?: () => void;
}

export function DeckorateModule({ onBack, initialView, onInitialViewConsumed }: Readonly<DeckorateModuleProps>) {
  const [view, setView] = useState<View>(initialView ?? "home");

  useEffect(() => {
    if (initialView) onInitialViewConsumed?.();
    // Runs once, on mount only — `initialView` is only meant to seed the
    // very first render of this instance.
  }, []);

  // Placeholder tab value, kept only so CommandPalette's props stay wired
  // for when a Design/Content/Assets/Brand UI lands.
  const [tab, setTab] = useState<Tab>("design");
  // "All actions" (Ctrl/Cmd+Shift+K or the home screen's card) is the pane's
  // one palette since ST-05 (AIVA-6805): AiVA's PowerPoint actions plus this
  // module's catalog, on every tab. The pane hands design actions back here
  // through subscribeDeckorateCommands below.
  // Self-dismissing toast for command failures surfacing after the "All
  // actions" palette has already closed (no palette left to notice).
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const actionNoticeTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (actionNoticeTimeoutRef.current !== null) window.clearTimeout(actionNoticeTimeoutRef.current);
    };
  }, []);
  const showActionNotice = useCallback((message: string) => {
    if (actionNoticeTimeoutRef.current !== null) window.clearTimeout(actionNoticeTimeoutRef.current);
    setActionNotice(message);
    actionNoticeTimeoutRef.current = window.setTimeout(() => setActionNotice(null), 3000);
  }, []);

  // "Asset Library" on the home grid (and its "All actions" row) opens the
  // Asset Library as a dialog
  // overlay rather than a routed view — it needs to stay reachable without
  // losing whatever view sits underneath it, the same reasoning the command
  // palette overlay already follows.
  //
  // In a real Office host the Asset Library lives entirely in a dialog window,
  // which stays open and drives its own loading/error/retry/insert states. The
  // launcher is just the pane's side of that conversation — it does the two
  // things a dialog physically cannot (authorise the fetch, run PowerPoint.run)
  // on request. `isInlineOpen` is only ever true where there's no Office Dialog
  // API at all: browser dev and tests.
  const {
    open: openAssetLibrary,
    isInlineOpen: assetLibraryInlineOpen,
    inlineTab: assetLibraryInlineTab,
    closeInline: closeAssetLibraryInline,
    paneNotice: assetLibraryNotice,
    paneNoticeSource: assetLibraryNoticeSource,
    dismissPaneNotice: dismissAssetLibraryNotice,
  } = useAssetLibraryLauncher(showActionNotice);

  // "Open Asset Library" on an insert-failure notice reopens the library on
  // the tab the failed insert came from (Slide or Presentation Templates); the
  // notice has done its job by then.
  const reopenAssetLibrary = useCallback(() => {
    dismissAssetLibraryNotice();
    openAssetLibrary(assetLibraryNoticeSource ?? "slide-templates");
  }, [assetLibraryNoticeSource, dismissAssetLibraryNotice, openAssetLibrary]);

  const assetLibraryNoticeCopy = resolveNoticeCopy(assetLibraryNotice, assetLibraryNoticeSource);
  const assetLibraryNoticeRemoved = assetLibraryNotice === "removed";
  // A removed template is a polite status (<output>); a failed insert is an assertive alert.
  const NoticeContainer = assetLibraryNoticeRemoved ? "output" : "div";

  // Apply Brand's own Saved Templates tab routes its eye icon here instead of
  // rendering its own preview — see brandTemplatePreviewHandoff.ts. Opening
  // the library is the same call either path takes; the staged template id
  // is what tells AssetLibraryContent/BrandTemplatesTab where to land.
  useEffect(() => subscribeBrandTemplatePreviewRequest(openAssetLibrary), [openAssetLibrary]);

  // Get Insights' "Make infographic" pill routes here instead of running the
  // conversion itself, so the user sees Convert to Infographic's own
  // progress UI. Non-null only while that hand-off is live — cleared on the
  // way back to home so a later, ordinary visit to Infographic (via the home
  // grid) doesn't inherit a stale description or auto-run.
  const [infographicSeed, setInfographicSeed] = useState<string | null>(null);

  // Every route back to the home grid runs through here — the panels' back
  // arrows and the Escape/Backspace gesture below alike — so no caller can
  // forget to drop Get Insights' staged description on the way out.
  const goHome = useCallback(() => {
    setInfographicSeed(null);
    setView("home");
  }, []);

  // Single entry point for every DeckorateNav-typed navigation (home grid
  // clicks and keyboard shortcuts alike). "asset-library" hands off to the
  // launcher, which prefers a real Office Dialog window (floats over all of
  // PowerPoint, not just the narrow task pane) and falls back to the in-pane
  // overlay when that can't work — same split create-slide uses between its
  // Launcher and Inline variants. Everything else is a routed View exactly as
  // before.
  const navigate = useCallback(
    (nav: DeckorateNav) => {
      if (nav === "asset-library") {
        openAssetLibrary();
        return;
      }
      setView(nav);
    },
    [openAssetLibrary],
  );

  // A chat pill can request a panel either while this module is unmounted (chat
  // is a different top-level tab — TaskPane switches tab, we mount, and claim
  // the target parked for us) or while it is already mounted (the user was
  // on the Deckorate tab — the subscription switches panels directly). Claiming
  // clears the park in both paths, so a handled target can't re-open later.
  //
  // Claiming MUST NOT happen in a useState initializer: consuming mutates
  // module state, and StrictMode (src/main.tsx) double-invokes render, so the
  // discarded pass would eat the target and strand the real one on Home. An
  // effect runs once per commit, and its StrictMode re-run is a no-op because
  // the park is already empty. useLayoutEffect rather than useEffect so the
  // switch lands before paint — otherwise Home flashes first.
  useLayoutEffect(() => {
    const parked = consumePendingDeckorateView();
    if (parked) setView(parked);
    return subscribeDeckorateNav((target) => {
      consumePendingDeckorateView();
      // A null target is a tab-only request (Code Mode's leader): mount this
      // module, but leave whatever panel the user was on exactly as it is.
      if (target) setView(target);
    });
  }, []);

  // Maps onNavigate onto `view` for VIEW_TABS values; otherwise just tracks
  // the placeholder tab state. "insights" gets its own check because it's
  // handled by a dedicated early-return branch below (like "share-export"),
  // not the generic VIEW_TABS/ActivePanel map.
  const handleNavigate = useCallback((next: Tab) => {
    // "asset-library" is a dialog overlay, not a panel — it must not become
    // `view` (there is no VIEW_PANELS entry) or `tab`, so it returns early.
    if (next === "asset-library") {
      openAssetLibrary();
      return;
    }
    setTab(next);
    if (next === "insights") {
      setView("insights");
      return;
    }
    if (isViewTab(next)) setView(next);
  }, [openAssetLibrary]);

  const currentTab: Tab = isViewTab(view) ? view : tab;

  // Runs a command from the "All actions" dialog (navigate/dom-click/dom-select
  // only — "pending"/"ai-preview" resolve inside the dialog, see AllActionsDialogApp.tsx).
  const runCatalogCommand = useCallback(
    async (cmd: DeckorateCommand) => {
      if (cmd.kind === "navigate" && cmd.tab) {
        handleNavigate(cmd.tab);
        return;
      }
      if (cmd.kind === "open-sheet" && cmd.sheet) {
        // Same bridge the in-pane palette uses — the sheet is on the chat tab.
        requestChatSheet(cmd.sheet);
        return;
      }
      try {
        await runDomAction(cmd, currentTab, handleNavigate, showActionNotice);
      } catch (e) {
        logDeckorateModuleError("cmd-palette-dialog-action-failed", "RunCatalogCommandError", e);
        showActionNotice(`Something went wrong running "${cmd.label}" — try again.`);
      }
    },
    [currentTab, handleNavigate, showActionNotice],
  );

  // Code Mode's dispatch target: catalogue commands go through the same path
  // as the "All actions" palette (runCatalogCommand); the Asset Library
  // special case mirrors navigate()'s own "asset-library" branch above.
  const runCodeModeEntry = useCallback(
    (entry: CodeModeEntry) => {
      if (entry.target.kind === "asset-library") {
        openAssetLibrary();
        return;
      }
      void runCatalogCommand(entry.target.command);
    },
    [openAssetLibrary, runCatalogCommand],
  );
  const codeMode = useCodeMode(runCodeModeEntry);

  /**
   * Escape and Backspace as one "back out of here" gesture, resolved as a
   * ladder — the first rung that applies wins and the rest never run:
   *
   * 1. **Focus is in a field** — leave the key alone. Backspace has to go on
   *    deleting characters, and Escape belongs to whatever the field does
   *    with it (Format Tools' search closes its dropdown on it). Code Mode's
   *    focus catcher is the exception: it is an `<input>` only because that
   *    is the one thing PowerPoint will hand focus to.
   * 2. **An overlay is open** — it closes first. This listener is
   *    capture-phase, so without this rung it would beat every modal's own
   *    bubble-phase Escape handler and tear the panel down around it.
   * 3. **A code is part-typed** — Escape drops the letters, Backspace drops
   *    the last one. Code mode stays on through both.
   * 4. **A panel is open** — go to the home grid. This is the rung the
   *    gesture exists for: `bc` opens Brand Check, Backspace comes back.
   *    It is deliberately not conditional on code mode being armed — a panel
   *    reached by any route backs out the same way.
   * 5. **Already home** — nothing to go back to, so the key falls through:
   *    the accumulator reads Escape as "end code mode" (still the only way
   *    out besides losing focus), and Backspace is left unconsumed.
   */
  const handleDismissKey = useCallback(
    (event: KeyboardEvent): boolean => {
      if (!isPlainBackKey(event)) return false;
      if (isAllActionsOpen() || assetLibraryInlineOpen || hasOpenDismissLayer()) return false;
      const bufferChanged = event.key === "Escape" ? codeModeStore.clearBuffer() : codeModeStore.backspace();
      if (bufferChanged) return true;
      if (view === "home") return false;
      goHome();
      // The panel holding focus is about to unmount, which would drop focus
      // on <body>. Pull it back to the catcher so the next code still lands
      // in the pane — but only while code mode is on, since a mouse-driven
      // visit to Home has no business moving focus into a hidden input.
      if (codeModeStore.getSnapshot().armed) focusCodeModeCatcher();
      return true;
    },
    [assetLibraryInlineOpen, goHome, view],
  );

  // Single keydown listener for both the legacy single-key nav shortcuts
  // (resolveShortcut) and the Code Mode chord accumulator — extends the
  // module's existing key handling rather than registering a competing
  // listener (TR-6).
  //
  // The nav shortcuts are resolved FIRST and win outright (AC-10: existing
  // shortcuts behave exactly as before). With the single Alt+Shift+K leader
  // the two systems no longer compete for the same keys at all — Code Mode
  // consumes only the leader and bare letters while armed, and every
  // Ctrl+Alt combination falls straight through to resolveShortcut. Order
  // still matters as a guarantee rather than a fix: whatever codes are added
  // later, a nav shortcut can never be shadowed.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const nav = resolveShortcut(event);
      if (nav) {
        event.preventDefault();
        navigate(nav);
        return;
      }
      // Escape still reaches the accumulator from inside a field so an armed
      // chord is always cancellable; letters do not, so a live chord can
      // never swallow the user's typing (BR-7). The one exception is Code
      // Mode's own focus catcher, which exists purely to hold focus after the
      // slide-side leader fires — it is editable only because a real input is
      // the only thing PowerPoint will hand focus to.
      if (event.key !== "Escape" && isTypingIntoField(event)) return;
      // Ahead of the accumulator: it would otherwise read Escape as "end code
      // mode" before the ladder ever got to treat it as "go back".
      if (handleDismissKey(event)) {
        event.preventDefault();
        return;
      }
      if (codeMode.handleKeyDown(event)) {
        event.preventDefault();
      }
    }
    // Capture phase, on `document` — deliberately matching
    // useCommandPalette's Ctrl+Shift+K listener rather than the
    // bubble-phase `window` listener this used to be. A bubble listener on
    // `window` is the last thing to see a keydown, so anything in between
    // calling stopPropagation() silently ate it: that is why Ctrl+Alt+N/B/D/K
    // never fired while the palette's capture-phase shortcut always did.
    // Code mode is sticky — it stays on between codes — so it must switch
    // off when the pane stops being where the user is typing. Otherwise the
    // cue lingers over a slide the keystrokes are actually going to, and the
    // mode silently reactivates the moment focus returns. Escape does the
    // same thing deliberately (see store.handleKeydown).
    function handleBlur() {
      codeModeStore.reset();
    }
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
      window.removeEventListener("blur", handleBlur);
    };
    // codeMode.handleKeyDown is stable (useCodeMode wraps it in useCallback
    // with no deps) — depending on it instead of the whole `codeMode` object
    // (a fresh literal every render) keeps this effect from tearing down and
    // re-adding the listener on every render.
  }, [navigate, codeMode.handleKeyDown, handleDismissKey]);

  // runCatalogCommand's identity changes with `currentTab`, which a dom-click
  // command can itself change mid-run via handleNavigate. Reading it through a
  // ref (not a subscription dependency) keeps the one subscription below.
  const runCatalogCommandRef = useRef(runCatalogCommand);
  runCatalogCommandRef.current = runCatalogCommand;

  // A design action chosen in the pane's "All actions" palette. Chosen from
  // another tab, it was parked while TaskPane switched here and is claimed as
  // this module mounts. Layout effect, like the nav claim above, so a
  // navigate lands before Home paints.
  useLayoutEffect(
    () => subscribeDeckorateCommands((cmd) => void runCatalogCommandRef.current(cmd)),
    [],
  );

  const assetLibrary = assetLibraryInlineOpen && <AssetLibraryDialog onClose={closeAssetLibraryInline} initialTab={assetLibraryInlineTab} />;

  const notice = actionNotice && (
    <output className={styles.actionToast}>
      {actionNotice}
    </output>
  );

  const ActivePanel = isViewTab(view) ? VIEW_PANELS[view] : null;

  // "Share & Export" opens the analyse/optimise/export panel.
  if (view === "share-export") {
    return <SharePanel onBack={goHome} />;
  }

  // "Get Insights" opens the Get Insights panel — analyses the currently
  // selected chart, table, or image and renders key findings.
  if (view === "insights") {
    return (
      <GetInsightsPanel
        onBack={goHome}
        onMakeInfographic={(description) => {
          setInfographicSeed(description);
          setView("infographic");
        }}
      />
    );
  }

  // Get Insights routed here with a seeded description — auto-run Convert
  // to Infographic instead of the plain onBack-only render further down, so
  // the user lands straight in its progress UI. A view visited normally
  // (home grid, shortcut) has no seed and falls through to the generic
  // ActivePanel render below, unaffected.
  if (view === "infographic" && infographicSeed !== null) {
    return (
      <InfographicPanel
        onBack={goHome}
        initialDescription={infographicSeed}
        autoRun
      />
    );
  }

  return (
    <div className={styles.panel}>
      {!ActivePanel && (
        <ModuleHeader
          title="Deckorate"
          subtitle="Slide Design"
          icon={DeckorateIcon}
          accent="deckorate"
          onBack={onBack}
        />
      )}
      {!ActivePanel && assetLibraryNotice && assetLibraryNoticeCopy && (
        <NoticeContainer
          className={assetLibraryNoticeRemoved ? `${styles.insertNotice} ${styles.insertNoticeWarning}` : styles.insertNotice}
          role={assetLibraryNoticeRemoved ? undefined : "alert"}
        >
          <div className={styles.insertNoticeText}>
            <div className={styles.insertNoticeTitle}>{assetLibraryNoticeCopy.title}</div>
            <div className={styles.insertNoticeMessage}>{assetLibraryNoticeCopy.message}</div>
            <button type="button" className={styles.insertNoticeAction} onClick={reopenAssetLibrary}>
              Open Asset Library
            </button>
          </div>
          <button type="button" className={styles.insertNoticeDismiss} aria-label="Dismiss" onClick={dismissAssetLibraryNotice}>
            ✕
          </button>
        </NoticeContainer>
      )}
      {ActivePanel ? (
        <ActivePanel onBack={goHome} />
      ) : (
        <DeckorateHome onNavigate={navigate} onOpenPalette={requestAllActions} />
      )}
      {notice}
      {assetLibrary}
    </div>
  );
}
